import { useState, useEffect } from "react";
import { Reservation } from "../types/reservation";
import { paths, API_URL } from "../config/endpoints";

export const useReservationDetails = (uid: string) => {
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const apiUrl = `${API_URL}/${paths.guest}/${paths.reservations}/${uid}`;

  useEffect(() => {
    const fetchReservationDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch reservation details");
        }

        const data = await response.json();
        setReservation(data);
      } catch (error: any) {
        setError(
          error.message ||
            "An error occurred while fetching reservation details"
        );
      } finally {
        setLoading(false);
      }
    };

    // Fetch the reservation details if the uid is provided
    if (uid) {
      fetchReservationDetails();
    }
  }, [uid, apiUrl]);

  // Function to update reservation details
  const updateReservation = async (
    updatedReservation: Partial<Reservation>
  ) => {
    setUpdateLoading(true);
    setUpdateError(null);

    try {
      const response = await fetch(apiUrl, {
        method: "PATCH", // Or "PUT" depending on your API specification
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(updatedReservation),
      });

      if (!response.ok) {
        throw new Error("Failed to update reservation");
      }

      const data = await response.json();
      setReservation(data); // Update the local state with the updated reservation
    } catch (error: any) {
      setUpdateError(
        error.message || "An error occurred while updating reservation"
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  return {
    reservation,
    loading,
    error,
    updateReservation, // Function to call when updating the reservation
    updateLoading,
    updateError,
  };
};
